import { createSelector, Selector } from 'reselect'

import { BundlesState } from './bundles.reducer'

import { Bundle, BundleType } from '../model/bundles.model'

import { Price } from '~/basket/model/common/price.model'
import { RootState } from '~/core/store/root.reducer'

type GetBundles = Selector<RootState, BundlesState>

export const getBundles: GetBundles = (state) => state.bundles

const getId = (_: RootState, id: string) => id

const getType = (_: RootState, __: string, type: BundleType) => type

const selectBundleCollection = createSelector(getBundles, getId, (bundles, id) => bundles[id])

export const selectBundles = createSelector(getBundles, getId, getType, (bundles, id, type) => {
  return bundles[id][type]
})

export const selectBundlesList = createSelector(selectBundles, (bundles) => {
  return bundles.data
})

export const selectBundlesListLoading = createSelector(selectBundles, (bundles) => {
  return bundles.loading
})

export const selectBundlesListError = createSelector(selectBundles, (bundles) => {
  return !!bundles.errorCode
})

export const selectSelectedBundlesIds = createSelector(selectBundles, (bundles) => {
  return bundles.selected
})

function findBundles(selectedIds: number[], bundles: Bundle[]) {
  let foundBundles: Bundle[] = []

  selectedIds.forEach((id) => {
    const foundBundle = bundles.find((bundle) => bundle.id === id)
    if (foundBundle) foundBundles = [...foundBundles, foundBundle]
  })

  return foundBundles
}

export const selectSelectedBundles = createSelector(
  selectBundlesList,
  selectSelectedBundlesIds,
  (bundles, selectedIds) => {
    let foundBundles: Bundle[] = []

    selectedIds.forEach((id) => {
      const foundBundle = bundles.find((bundle) => bundle.id === id)
      if (foundBundle) foundBundles = [...foundBundles, foundBundle]
    })

    return foundBundles
  },
)

export const selectAllBonusesSelected = createSelector(selectBundleCollection, (collection) =>
  findBundles(collection[BundleType.BONUS].selected, collection[BundleType.BONUS].data),
)

export const selectAllPromosSelected = createSelector(selectBundleCollection, (collection) =>
  findBundles(collection[BundleType.PROMOS].selected, collection[BundleType.PROMOS].data),
)

export const selectAllBundlesSelected = createSelector(selectBundleCollection, (collection) => {
  const bonusSelected = findBundles(
    collection[BundleType.BONUS].selected,
    collection[BundleType.BONUS].data,
  )
  const promosSelected = findBundles(
    collection[BundleType.PROMOS].selected,
    collection[BundleType.PROMOS].data,
  )

  return [...bonusSelected, ...promosSelected]
})

function flatten(prices: Price[][]) {
  return prices.reduce((a: Price[], b: Price[]) => a.concat(b), [])
}

export const selectSelectedBundlesPrice = createSelector(selectAllBundlesSelected, (bundles) => {
  const prices = bundles.map((elem) => elem?.price || [])
  return flatten(prices)
})

export const selectHasSelectedBundles = createSelector(
  selectAllBundlesSelected,
  (bundles) => !!bundles.length,
)
